import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { LocationSuggestionsResult } from 'lib/extensionMessaging';

const LOCATION_SEARCH_DEBOUNCE_MS = 1000;
const LOCATION_SEARCH_MIN_CHARS = 2;

type ExtensionLocationAutocompleteProps = {
  value: string;
  onChange: (next: string) => void;
  extensionInstalled: boolean;
  platformLabel: string;
  placeholder: string;
  helperText?: string;
  fetchSuggestions: (query: string) => Promise<LocationSuggestionsResult>;
  /** Extra key to re-run search when caller context changes (e.g. LinkedIn remote toggle). */
  searchKey?: string | boolean | number;
};

const ExtensionLocationAutocomplete = ({
  value,
  onChange,
  extensionInstalled,
  platformLabel,
  placeholder,
  helperText,
  fetchSuggestions,
  searchKey,
}: ExtensionLocationAutocompleteProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const fetchSuggestionsRef = useRef(fetchSuggestions);
  fetchSuggestionsRef.current = fetchSuggestions;

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const trimmedInput = inputValue.trim();

    if (!extensionInstalled || trimmedInput.length < LOCATION_SEARCH_MIN_CHARS) {
      setOptions([]);
      setLoading(false);
      setSearchError(null);
      return;
    }

    const currentRequestId = requestIdRef.current + 1;
    requestIdRef.current = currentRequestId;

    const timer = window.setTimeout(async () => {
      setLoading(true);
      setSearchError(null);

      const result = await fetchSuggestionsRef.current(trimmedInput);

      if (requestIdRef.current !== currentRequestId) {
        return;
      }

      setLoading(false);

      if (result.extensionNotRunning) {
        setOptions([]);
        setSearchError(result.error || null);
        return;
      }

      if (!result.ok) {
        setOptions([]);
        setSearchError(result.error || `${platformLabel} location search failed`);
        return;
      }

      setOptions(result.locations || []);
    }, LOCATION_SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timer);
    };
  }, [extensionInstalled, inputValue, platformLabel, searchKey]);

  const noOptionsText = !extensionInstalled
    ? `Install the Harbor extension to search ${platformLabel} locations`
    : inputValue.trim().length < LOCATION_SEARCH_MIN_CHARS
      ? 'Type at least 2 characters'
      : searchError || 'No matching locations found';

  return (
    <Autocomplete
      options={options}
      value={value || null}
      inputValue={inputValue}
      loading={loading}
      filterOptions={locationOptions => locationOptions}
      getOptionLabel={option => option}
      isOptionEqualToValue={(option, optionValue) => option === optionValue}
      onInputChange={(_, nextInputValue, reason) => {
        if (reason === 'input' || reason === 'clear') {
          setInputValue(nextInputValue);
        }
      }}
      onChange={(_, next) => onChange(next || '')}
      noOptionsText={noOptionsText}
      renderInput={params => (
        <TextField
          {...params}
          size="small"
          placeholder={placeholder}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default ExtensionLocationAutocomplete;
